// Markdown leve + marcadores estendidos compartilhados entre painéis.
//
// Cobre `**bold**`, `*italic*`, `==realce==`, `++sublinhado++`, `~~tachado~~`,
// `` `code` ``, links `[label](url)`, e marcadores semânticos de cor
// `{{color:nome}}...{{/color}}`. KaTeX (math) NÃO é renderizado aqui — o
// caller chama `renderMath` (em `./math`) depois de injetar o HTML no DOM.
//
// Extraído de student-new/StudyWorkspace.tsx para ser reaproveitado por:
//   - apps/ui/components/question-preview.tsx (admin/teacher veem o mesmo
//     que o aluno vê)
//   - student-new (mantém o re-export)
//   - manager-new (preview do upload)

// Paleta de cores semânticas — 8 canônicas (tailwind-600) + sinônimos PT-BR
// pra que QUALQUER variação razoável que o banco use renderize colorida em
// vez de mostrar `[xyz]A[/xyz]` literal. Mantido em sync com a paleta de
// `apps/student-new/src/StudyWorkspace.tsx`.
export const COLOR_TOKENS: Record<string, string> = {
  azul: "#2563eb",
  vermelho: "#dc2626",
  verde: "#059669",
  amarelo: "#ca8a04",
  laranja: "#ea580c",
  roxo: "#7c3aed",
  rosa: "#db2777",
  cinza: "#6b7280",
  preto: "#18181b",
  branco: "#737373",
  marrom: "#92400e",
  azulado: "#2563eb",
  azul_claro: "#3b82f6",
  azul_escuro: "#1e3a8a",
  navy: "#1e3a8a",
  marinho: "#1e3a8a",
  indigo: "#4f46e5",
  ciano: "#0891b2",
  turquesa: "#0d9488",
  vermelhinho: "#dc2626",
  vermelhao: "#b91c1c",
  bordo: "#9f1239",
  vinho: "#9f1239",
  escarlate: "#dc2626",
  carmim: "#dc2626",
  esverdeado: "#059669",
  esmeralda: "#10b981",
  verde_claro: "#10b981",
  verde_escuro: "#047857",
  oliva: "#65a30d",
  limao: "#84cc16",
  amarelado: "#ca8a04",
  dourado: "#ca8a04",
  ouro: "#ca8a04",
  bege: "#a8a29e",
  alaranjado: "#ea580c",
  ambar: "#d97706",
  ferrugem: "#c2410c",
  arroxeado: "#7c3aed",
  lilas: "#a855f7",
  violeta: "#7c3aed",
  purpura: "#7c2d92",
  magenta: "#c026d3",
  fucsia: "#d946ef",
  rosado: "#db2777",
  acinzentado: "#6b7280",
  prata: "#94a3b8",
  prateado: "#94a3b8",
  grafite: "#374151",
  chumbo: "#475569",
  bronze: "#92400e"
};

const COLOR_CANON_NAMES = [
  "azul", "vermelho", "verde", "amarelo", "laranja", "roxo", "rosa", "cinza",
  "preto", "branco", "marrom", "ciano", "turquesa", "lilas", "violeta",
  "bordo", "vinho", "dourado", "bege", "ambar", "magenta", "prata", "navy"
];
const COLOR_ACCENT_RE = new RegExp("[\\u0300-\\u036f]", "g");
const COLOR_STEM_STRIPS = [
  /^a/,
  /[_-]?claro$/, /[_-]?escuro$/, /[_-]?intenso$/, /[_-]?vivo$/,
  /ado$/, /ido$/, /oso$/, /inho$/, /zinho$/, /ada$/
];
export function normalizeColorName(raw: string): string | null {
  const clean = raw.toLowerCase().normalize("NFD").replace(COLOR_ACCENT_RE, "").replace(/\s+/g, "_");
  if (COLOR_TOKENS[clean]) return clean;
  for (const canon of COLOR_CANON_NAMES) if (clean.includes(canon)) return canon;
  for (const re of COLOR_STEM_STRIPS) {
    const stem = clean.replace(re, "");
    if (stem !== clean && stem.length >= 3) {
      if (COLOR_TOKENS[stem]) return stem;
      for (const canon of COLOR_CANON_NAMES) if (stem.includes(canon)) return canon;
    }
  }
  return null;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Blocos de matemática (KaTeX) precisam chegar ao DOM como UM nó de texto
// contínuo, sem `<br>` no meio nem regex de bold/italic transformando o
// LaTeX. A estratégia é extrair os blocos antes de qualquer transformação,
// substituir por um placeholder ASCII inerte, rodar o pipeline normal e
// restaurar no final com escape mínimo (só `&` e `<` pra HTML não confundir
// — o browser decodifica esses no textContent, que é o que KaTeX lê).
const BLOCK_TOKEN_RE = /BLK(\d+)/g;
const INLINE_TOKEN_RE = /INL(\d+)/g;

function escapeMathForHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}

/**
 * Pré-processamento conservador: o extrator às vezes envolve marcadores
 * `{{color:...}}...{{/color}}` em `$...$` por engano (color marker NÃO é
 * matemática). Se passar pela proteção de math, o KaTeX renderiza o bloco
 * como texto itálico e come as chaves duplas como grupos, perdendo a cor.
 * Removemos `$` adjacentes a `{{color:...}}` e `{{/color}}` antes de QUALQUER
 * outro processamento. Não toca em math legítima.
 */
function stripDollarsAroundColorMarkers(s: string): string {
  return s
    .replace(/\$(\{\{color:[a-z]+\}\})/gi, "$1")
    .replace(/(\{\{\/color\}\})\$/g, "$1");
}

/**
 * Fix defensivo para escapes LaTeX mal-formados no JSON: quando o autor
 * escreve `"\text{x}"` (um backslash) em vez de `"\\text{x}"`, `JSON.parse`
 * interpreta `\t` como TAB (U+0009) e o resto vira texto solto. KaTeX
 * renderiza esse texto solto em itálico no meio da equação. O sintoma é
 * "exte" ou "ext{ e }" aparecendo no meio de uma fórmula.
 *
 * Detectamos chars de controle (TAB, BS, FF, VT, NL inesperado dentro de
 * math…) seguidos da continuação esperada de um comando LaTeX comum e
 * restauramos o backslash. Conservador: só toca em sequências onde a
 * continuação bate com um nome conhecido, evitando reescrever conteúdo
 * legítimo.
 */
// Mapeamento (char-de-controle-na-string → comando LaTeX original). Usamos
// `new RegExp(...)` em vez de regex literal porque CR (U+000D) e LF (U+000A)
// terminam um literal /.../.
const BOTCHED_LATEX_RECOVERY: Array<[RegExp, string]> = [
  [/	ext\b/g, "\\text"],
  [/	imes\b/g, "\\times"],
  [/	heta\b/g, "\\theta"],
  [/	an\b/g, "\\tan"],
  [/	au\b/g, "\\tau"],
  [/	op\b/g, "\\top"],
  [/	o\b/g, "\\to"],
  [/	g\b/g, "\\tg"],
  [/eta\b/g, "\\beta"],
  [/ig\b/g, "\\big"],
  [/ullet\b/g, "\\bullet"],
  [/ot\b/g, "\\bot"],
  [/inom\b/g, "\\binom"],
  [/rac\b/g, "\\frac"],
  [/orall\b/g, "\\forall"],
  [/ec\b/g, "\\vec"],
  [/arepsilon\b/g, "\\varepsilon"],
  [/arphi\b/g, "\\varphi"],
  [new RegExp("\\u000Dightarrow\\b", "g"), "\\rightarrow"],
  [new RegExp("\\u000Dangle\\b", "g"), "\\rangle"],
  [new RegExp("\\u000Dho\\b", "g"), "\\rho"],
  [new RegExp("\\u000Aeq\\b", "g"), "\\neq"],
  [new RegExp("\\u000Aabla\\b", "g"), "\\nabla"],
  [new RegExp("\\u000Aeg\\b", "g"), "\\neg"]
];

function recoverBotchedLatexEscapes(s: string): string {
  let out = s;
  for (const [pattern, replacement] of BOTCHED_LATEX_RECOVERY) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

function preprocessForMath(s: string): string {
  return recoverBotchedLatexEscapes(stripDollarsAroundColorMarkers(s));
}

export function renderInline(raw: string): string {
  // 0) Pré-processo: corrige escapes LaTeX que o JSON.parse converteu em
  //    chars de controle (\t→TAB etc.) e remove `$` indevidos em volta de
  //    markers de cor.
  const stripped = preprocessForMath(raw);
  // 1) Protege blocos de matemática INLINE antes de qualquer transformação:
  //    `\(...\)` e `$...$` (single, sem newline). O bloco display `$$...$$`
  //    é tratado em `renderBlock` porque pode atravessar linhas/parágrafos.
  const tokens: string[] = [];
  let s = stripped.replace(/\\\(([\s\S]+?)\\\)/g, (m) => {
    tokens.push(m);
    return `INL${tokens.length - 1}`;
  });
  s = s.replace(/(?<![\\$])\$([^\n$]+?)\$(?!\$)/g, (m) => {
    tokens.push(m);
    return `INL${tokens.length - 1}`;
  });
  s = escapeHtml(s);
  // ORDEM IMPORTA: marcadores estendidos primeiro (==, ++, ~~) pra não
  // colidirem com bold (**), italic (*) ou underscore-italic.
  // `code` — escapes preservados pq escapeHtml já rodou.
  s = s.replace(
    /`([^`\n]+)`/g,
    '<code class="rounded bg-accent px-1 py-0.5 text-[0.92em]">$1</code>'
  );
  // Shim de tolerância: bancos extraídos por LLM frequentemente vêm com
  // `[cor]texto[/cor]` em vez do marcador oficial `{{color:cor}}…{{/color}}`.
  // Aceitamos qualquer palavra de 2-24 chars; `normalizeColorName` mapeia
  // sinônimos PT-BR (lilás→roxo, ciano→azul, arroxeado→roxo). Restritivo o
  // suficiente pra não comer markdown link/footnote.
  s = s.replace(
    /\[\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]([\s\S]*?)\[\s*\/\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]/g,
    (_full, openName: string, inner: string) => {
      const canonical = normalizeColorName(openName);
      if (canonical) return `{{color:${canonical}}}${inner}{{/color}}`;
      return `<strong>${inner}</strong>`;
    }
  );
  // {{color:nome}}texto{{/color}} — marcadores semânticos de cor.
  s = s.replace(
    /\{\{color:([a-zA-ZÀ-ÿ_-]+)\}\}([\s\S]*?)\{\{\/color\}\}/gi,
    (_m, name: string, inner: string) => {
      const canonical = normalizeColorName(name) ?? name.toLowerCase();
      const c = COLOR_TOKENS[canonical];
      if (!c) return inner;
      return `<span style="color:${c};font-weight:600;">${inner}</span>`;
    }
  );
  // ==realce== (highlight/mark)
  s = s.replace(/==([^=\n]+)==/g, "<mark>$1</mark>");
  // ++sublinhado++
  s = s.replace(/\+\+([^+\n]+)\+\+/g, "<u>$1</u>");
  // ~~tachado~~
  s = s.replace(/~~([^~\n]+)~~/g, "<s>$1</s>");
  // **negrito** (e __negrito__ alternativo)
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  // *italic* — não pega ** já consumido
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  // _italic_ — só se cercado por whitespace/início/pontuação pra não destruir
  // T_f, x_1 etc.
  s = s.replace(
    /(^|[\s>([{,;:!?])_([^_\n\\^]+)_(?=[\s<)\]}.,;:!?]|$)/g,
    "$1<em>$2</em>"
  );
  // ![alt](url) — imagem inline. Aceita http(s) e data:image. PRECISA vir
  // antes do regex de link senão `[alt](url)` come o `!`. URL pode conter
  // qualquer char até `)` não-escapado (data URLs têm `;`, `,`, `+`, `/`).
  s = s.replace(
    /!\[([^\]\n]*)\]\(((?:https?:\/\/|data:image\/)[^\s)]+)\)/gi,
    (_m, alt: string, url: string) => {
      const safeAlt = alt.replace(/"/g, "&quot;");
      return `<img src="${url}" alt="${safeAlt}" class="my-2 inline-block max-w-full rounded-md border" />`;
    }
  );
  // [label](url) — só http/https pra evitar javascript: e similares.
  s = s.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline">${label}</a>`
  );
  // Restaura blocos inline de math protegidos no início.
  s = s.replace(INLINE_TOKEN_RE, (_m, idx: string) =>
    escapeMathForHtml(tokens[Number(idx)] ?? "")
  );
  return s;
}

// Heurística local de quebra de parágrafo — ZERO IA, ZERO chamada de rede.
// Quando o JSON do banco vem com a explicação inteira em um único bloco de
// texto sem `\n\n`, vira uma parede que dificulta a leitura. Detectamos
// conectores que tipicamente iniciam um novo passo de raciocínio (Assim,
// Logo, Portanto, Aplicando, Como…) e inserimos `\n\n` antes deles, *desde
// que* o parágrafo já esteja suficientemente longo e o último break tenha
// sido faz tempo (>= MIN_CHARS_BETWEEN_BREAKS) — assim evitamos fragmentar
// textos curtos ou inserir breaks consecutivos.
//
// IMPORTANTE: chamado *depois* de proteger display math (`$$...$$` e
// `\[...\]`), então não rasga blocos KaTeX. Inline math (`\(...\)`/`$...$`)
// é tratado pelo `renderInline` por linha — o break só insere `\n\n`,
// nunca dentro de uma frase com `\(...\)`.
const SOFT_BREAK_TRIGGERS = [
  "Assim,",
  "Logo,",
  "Portanto,",
  "Aplicando",
  "Como ",
  "Daí,",
  "Em seguida,",
  "Substituindo",
  "Note que ",
  "Observe que ",
  "Resolvendo,",
  "Concluímos",
  "Conclui-se",
  "Por exemplo,",
  "Alternativa "
];
const MIN_PARA_LEN_FOR_SOFT_BREAK = 220;
const MIN_CHARS_BETWEEN_BREAKS = 120;

function applySoftParagraphBreaks(s: string): string {
  if (s.length < MIN_PARA_LEN_FOR_SOFT_BREAK) return s;
  // Trabalha por bloco já delimitado por linha em branco — só atua em
  // parágrafos longos, deixa os já bem formatados em paz.
  return s
    .split(/\n{2,}/)
    .map((para) => {
      if (para.length < MIN_PARA_LEN_FOR_SOFT_BREAK) return para;
      // Acha posições candidatas onde inserir break. Cada candidata é um
      // índice JUST AFTER a pontuação `. ` / `! ` / `? ` que precede um
      // trigger. Inserimos sequencialmente respeitando distância mínima.
      const breaks: number[] = [];
      const triggerAlt = SOFT_BREAK_TRIGGERS.map((t) =>
        t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      ).join("|");
      const re = new RegExp(`([.!?])\\s+(?=(?:${triggerAlt}))`, "g");
      let m: RegExpExecArray | null;
      let lastBreak = 0;
      while ((m = re.exec(para)) !== null) {
        const insertAt = m.index + m[0].length;
        if (insertAt - lastBreak < MIN_CHARS_BETWEEN_BREAKS) continue;
        if (para.length - insertAt < 40) continue; // sem cauda curta
        breaks.push(insertAt);
        lastBreak = insertAt;
      }
      if (breaks.length === 0) return para;
      // Reconstrói inserindo `\n\n` nas posições marcadas (do fim pro
      // começo pra não deslocar índices).
      let out = para;
      for (let i = breaks.length - 1; i >= 0; i--) {
        const idx = breaks[i];
        out = out.slice(0, idx).replace(/\s+$/, "") + "\n\n" + out.slice(idx);
      }
      return out;
    })
    .join("\n\n");
}

export function renderBlock(raw: string): string {
  // 1) Protege display math (`$$...$$` e `\[...\]`) que pode atravessar
  //    linhas/parágrafos. Sem isso, o split por `\n{2,}` + `<br>` por linha
  //    quebraria o bloco entre nós de texto e o KaTeX auto-render não casaria
  //    `$$` de abertura com o de fechamento.
  const SOH = String.fromCharCode(1);
  const blocks: string[] = [];
  // 0) Mesma defesa: corrige escapes LaTeX viralados em chars de controle
  //    (TAB/BS/FF de `\t`, `\b`, `\f` em JSON) e strip de `$` em volta de
  //    color markers que eventualmente apareçam fora de blocos display.
  let work = preprocessForMath(raw).replace(
    /\$\$[\s\S]+?\$\$/g,
    (m) => {
    blocks.push(m);
    return `${SOH}BLK${blocks.length - 1}${SOH}`;
  });
  work = work.replace(/\\\[[\s\S]+?\\\]/g, (m) => {
    blocks.push(m);
    return `${SOH}BLK${blocks.length - 1}${SOH}`;
  });

  // 1.5) Soft paragraph breaks — heurística PURAMENTE LOCAL (sem IA) pra
  //      quebrar parágrafos densos quando o JSON do banco veio com um
  //      bloco único de explicação. Aplicado apenas em parágrafos longos
  //      e antes de conectores que normalmente iniciam novo passo de
  //      raciocínio (Assim, Logo, Portanto, Aplicando, Como, etc.).
  work = applySoftParagraphBreaks(work);

  // 2) Quebra em parágrafos por linha em branco, aplica inline em cada.
  const paragraphs = work
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  let html = paragraphs
    .map((p) => {
      // Dentro do parágrafo, \n vira <br>.
      const lines = p.split("\n").map(renderInline).join("<br>");
      return `<p>${lines}</p>`;
    })
    .join("");

  // 3) Restaura blocos display com escape mínimo. O KaTeX auto-render lê
  //    `textContent` (que é decodificado pelo browser), então `&amp;=` no
  //    HTML vira `&=` no nó de texto e o KaTeX casa o `aligned`.
  html = html.replace(BLOCK_TOKEN_RE, (_m, idx: string) =>
    escapeMathForHtml(blocks[Number(idx)] ?? "")
  );
  return html;
}
